import { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaUserCircle,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaBriefcase,
  FaCheckCircle,
  FaClock,
  FaWallet,
} from "react-icons/fa";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { BaseUrl } from "../../../../utilities/BaseUrl";
import { FaArrowTrendDown, FaArrowTrendUp } from "react-icons/fa6";

/* ============================================
   Dummy Data User Detail
   ============================================ */
const DUMMY_USER = {
  id: "user-001",
  name: "Andri Ardiansyah",
  email: "andri@danelanet.com",
  phone_number: "0812-3456-7890",
  role: { name: "Super Admin" },
  status: { name: "Aktif" },
  image: null, // URL gambar jika ada
  address: "Jl. Raya Serang No. 12, Kota Serang, Banten",
  join_date: "2022-01-15",
  financials: {
    total_piutang: 1500000,
    total_hutang: 500000,
    history: [
      { id: "f-1", date: "2023-10-20", type: "Piutang", desc: "Pinjaman Modal Awal", amount: 1000000 },
      { id: "f-2", date: "2023-10-25", type: "Hutang", desc: "Potongan Gaji Bulan Lalu", amount: 500000 },
      { id: "f-3", date: "2023-11-01", type: "Piutang", desc: "Kasbon untuk Perbaikan Kendaraan", amount: 500000 },
    ],
  },
  jobs: {
    total_task: 45,
    completed: 38,
    in_progress: 7,
    recent_tasks: [
      { id: "t-1", code: "TKT-A1B2C3D", title: "Perbaikan Kabel ODP Blok A", status: "Selesai", date: "2023-10-28" },
      { id: "t-2", code: "TKT-E4F5G6H", title: "Instalasi Baru Pelanggan Paket 30Mbps", status: "Dalam Pengerjaan", date: "2023-10-29" },
      { id: "t-3", code: "TKT-I7J8K9L", title: "Ganti Router Pelanggan VIP", status: "Dalam Pengerjaan", date: "2023-10-30" },
    ],
  },
  salary: {
    base_salary: 4500000,
    bonuses: [
      { desc: "Bonus Target Instalasi", amount: 1000000 },
      { desc: "Lembur Minggu Ketiga", amount: 250000 },
    ],
    deductions: [
      { desc: "BPJS Kesehatan", amount: 150000 },
      { desc: "Potongan Kasbon", amount: 500000 },
    ],
  },
};

/* ============================================
   Sub-Komponen: SectionCard
   ============================================ */
const SectionCard = ({ title, icon: Icon, children }) => (
  <article
    className="rounded-2xl p-5 sm:p-6 transition-all duration-300"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
      backdropFilter: "blur(12px)",
    }}
  >
    <div className="flex items-center gap-3 mb-5">
      {Icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            background: "rgba(0, 180, 216, 0.1)",
            border: "1px solid rgba(0, 180, 216, 0.2)",
          }}
        >
          <Icon className="text-ocean-400 text-sm" />
        </div>
      )}
      <h3 className="font-display text-base sm:text-lg font-bold text-gray-100">
        {title}
      </h3>
    </div>
    {children}
  </article>
);

/* ============================================
   Sub-Komponen: StatusBadge (Sama seperti di List)
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "Aktif" || status === "Selesai";
  const isInProgress = status === "Dalam Pengerjaan";
  
  let styleClass = "bg-slate-500/12 text-slate-400 border-slate-500/20";
  if (isActive) styleClass = "bg-emerald-500/12 text-emerald-400 border-emerald-500/20";
  if (isInProgress) styleClass = "bg-amber-500/12 text-amber-400 border-amber-500/20";

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${styleClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : isInProgress ? "bg-amber-400" : "bg-slate-400"}`} />
      {status}
    </span>
  );
};

/* ============================================
   Format Currency
   ============================================ */
const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/* ============================================
   Halaman Utama: UserDetail
   ============================================ */
export const UserDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showSidebar, setShowSidebar] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulasi fetch API berdasarkan ID
    setTimeout(() => {
      setUser(DUMMY_USER);
      setIsLoading(false);
    }, 800);
  }, [id]);

  if (isLoading || !user) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-slate-600 border-t-ocean-400 animate-spin" />
            <p className="text-slate-400">Memuat data pengguna...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Kalkulasi Gaji Bersih
  const totalBonus = user.salary.bonuses.reduce((sum, b) => sum + b.amount, 0);
  const totalDeduction = user.salary.deductions.reduce((sum, d) => sum + d.amount, 0);
  const netSalary = user.salary.base_salary + totalBonus - totalDeduction;

  return (
    <>
      <Helmet>
        <title>Detail Pengguna {user.name} - DANELA NET</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
            >
              <FaArrowLeft />
            </button>
            <div className="flex flex-col">
              <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                Detail Pengguna
              </h2>
              <p className="text-xs text-slate-500 hidden md:block mt-0.5">
                Informasi lengkap dan riwayat karyawan
              </p>
            </div>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">
          
          {/* Profile & Financial Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Card Profil Utama */}
            <div className="lg:col-span-1">
              <SectionCard title="Profil Pengguna" icon={FaUserCircle}>
                <div className="flex flex-col items-center text-center">
                  {!user?.image ? (
                    <div
                      className="w-24 h-24 rounded-2xl flex items-center justify-center font-bold text-3xl flex-shrink-0 mb-4"
                      style={{
                        background: "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                        border: "1px solid rgba(0, 180, 216, 0.15)",
                        color: "#48CAE4",
                      }}
                    >
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                  ) : (
                    <img
                      src={`${BaseUrl}/storage/${user.image}`}
                      alt={user.name}
                      className="w-24 h-24 rounded-2xl object-cover flex-shrink-0 mb-4 border-2 border-slate-700"
                    />
                  )}
                  
                  <h2 className="text-xl font-bold text-gray-100">{user.name}</h2>
                  <div className="mt-2 mb-4">
                    <StatusBadge status={user?.status?.name} />
                  </div>

                  <div className="w-full space-y-3 text-left mt-2">
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaBriefcase className="text-ocean-400 w-5" />
                      <span className="text-slate-200 font-medium">{user?.role?.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaEnvelope className="text-ocean-400 w-5" />
                      <span className="text-slate-300">{user.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaPhone className="text-ocean-400 w-5" />
                      <span className="text-slate-300">{user.phone_number}</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-slate-400">
                      <FaMapMarkerAlt className="text-ocean-400 w-5 mt-0.5" />
                      <span className="text-slate-300">{user.address}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaCalendarAlt className="text-ocean-400 w-5" />
                      <span className="text-slate-300">Bergabung: {user.join_date}</span>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Hutang & Piutang */}
            <div className="lg:col-span-2">
              <SectionCard title="Data Hutang & Piutang" icon={FaMoneyBillWave}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {/* Piutang */}
                  <div className="p-4 rounded-xl" style={{ background: "rgba(16, 185, 129, 0.05)", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Total Piutang</span>
                      <FaArrowTrendUp className="text-emerald-400" />
                    </div>
                    <p className="text-2xl font-bold text-emerald-400">{formatCurrency(user.financials.total_piutang)}</p>
                  </div>
                  {/* Hutang */}
                  <div className="p-4 rounded-xl" style={{ background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Total Hutang</span>
                      <FaArrowTrendDown className="text-red-400" />
                    </div>
                    <p className="text-2xl font-bold text-red-400">{formatCurrency(user.financials.total_hutang)}</p>
                  </div>
                </div>

                {/* Tabel Riwayat */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700/50">
                        <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tanggal</th>
                        <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipe</th>
                        <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Keterangan</th>
                        <th className="pb-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Jumlah</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/30">
                      {user.financials.history.map((item) => (
                        <tr key={item.id}>
                          <td className="py-3 text-slate-400 whitespace-nowrap">{item.date}</td>
                          <td className="py-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${item.type === 'Piutang' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                              {item.type}
                            </span>
                          </td>
                          <td className="py-3 text-slate-300 font-medium">{item.desc}</td>
                          <td className={`py-3 text-right font-semibold whitespace-nowrap ${item.type === 'Piutang' ? 'text-emerald-400' : 'text-red-400'}`}>
                            {item.type === 'Piutang' ? '+' : '-'} {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>
          </div>

          {/* Kerjaan & Gaji */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Data Kerjaan/Tugas */}
            <SectionCard title="Riwayat Pekerjaan" icon={FaBriefcase}>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="text-center p-3 rounded-xl bg-slate-800/40">
                  <p className="text-2xl font-bold text-gray-100">{user.jobs.total_task}</p>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mt-1">Total Tugas</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                  <p className="text-2xl font-bold text-emerald-400">{user.jobs.completed}</p>
                  <p className="text-[10px] font-semibold text-emerald-500 uppercase mt-1">Selesai</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
                  <p className="text-2xl font-bold text-amber-400">{user.jobs.in_progress}</p>
                  <p className="text-[10px] font-semibold text-amber-500 uppercase mt-1">Proses</p>
                </div>
              </div>

              <div className="space-y-3">
                {user.jobs.recent_tasks.map((task) => (
                  <div 
                    key={task.id} 
                    className="p-3 rounded-xl flex items-center justify-between"
                    style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${task.status === 'Selesai' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                        {task.status === 'Selesai' ? <FaCheckCircle /> : <FaClock />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-100 truncate">{task.title}</p>
                        <p className="text-xs text-slate-500 font-mono">{task.code} • {task.date}</p>
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <StatusBadge status={task.status} />
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Data Gaji */}
            <SectionCard title="Informasi Gaji" icon={FaWallet}>
              <div className="p-4 rounded-xl mb-6 text-center" style={{ background: "linear-gradient(135deg, rgba(0, 180, 216, 0.1), rgba(0, 119, 182, 0.05))", border: "1px solid rgba(0, 180, 216, 0.2)" }}>
                <p className="text-xs font-semibold text-ocean-400 uppercase tracking-wider mb-1">Gaji Bersih Bulan Ini</p>
                <p className="text-3xl font-bold text-gray-100 tracking-tight">{formatCurrency(netSalary)}</p>
              </div>

              <div className="space-y-4">
                {/* Gaji Pokok */}
                <div className="flex justify-between items-center pb-3 border-b border-slate-700/30">
                  <span className="text-sm text-slate-400">Gaji Pokok</span>
                  <span className="text-sm font-semibold text-gray-100">{formatCurrency(user.salary.base_salary)}</span>
                </div>

                {/* Bonus */}
                <div>
                  <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">Tambahan / Bonus</p>
                  {user.salary.bonuses.map((item, index) => (
                    <div key={index} className="flex justify-between items-center mb-1.5">
                      <span className="text-sm text-slate-400 pl-2">• {item.desc}</span>
                      <span className="text-sm font-semibold text-emerald-400">+ {formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>

                {/* Potongan */}
                <div>
                  <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">Potongan</p>
                  {user.salary.deductions.map((item, index) => (
                    <div key={index} className="flex justify-between items-center mb-1.5">
                      <span className="text-sm text-slate-400 pl-2">• {item.desc}</span>
                      <span className="text-sm font-semibold text-red-400">- {formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          </div>

        </main>
      </DashboardLayout>
    </>
  );
};